import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPartner, getReports, getTierProgression } from '../api';
import type { Partner, ProductionReport, TierProgression } from '../types';
import PartnerActivityForm from '../components/PartnerActivityForm';

const CERTIFIED_THRESHOLD = 50000;

export default function PartnerDetail() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [reports, setReports] = useState<ProductionReport[]>([]);
  const [tierProgression, setTierProgression] = useState<TierProgression | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    if (!partnerId) return;
    setLoading(true);
    Promise.all([
      getPartner(partnerId),
      getReports(partnerId),
      getTierProgression(partnerId),
    ])
      .then(([p, r, t]) => {
        setPartner(p);
        setReports(r.reports);
        setTierProgression(t);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (partnerId) refresh();
    else setLoading(false);
  }, [partnerId]);

  if (loading) return <div className="text-gold/80">Loading…</div>;
  if (error) return <div className="text-red-400">Error: {error}</div>;
  if (!partner) return <div className="text-station-muted">Partner not found.</div>;

  const revenue = tierProgression?.total_collected_revenue ?? 0;
  const threshold = tierProgression?.threshold ?? CERTIFIED_THRESHOLD;
  const advancementPct = partner.tier === 'Associate' ? Math.min(100, Math.round((revenue / threshold) * 100)) : 100;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gold">{partner.name}</h1>
        <p className="text-station-muted mt-1">
          {partner.tier} · {partner.state_territory} · {partner.status}
        </p>
        <p className="text-sm text-station-muted mt-0.5">{partner.email}</p>
      </div>

      {/* Advancement Path: progress bar toward $50k Certified Broker threshold (Associates only) */}
      {partner.tier === 'Associate' && (
        <section className="rounded-xl border border-leather bg-leather/40 p-5">
          <h2 className="text-lg font-semibold text-gold mb-2">Advancement Path</h2>
          <p className="text-sm text-station-muted mb-3">
            Progress toward Certified Broker (${threshold.toLocaleString()} collected revenue)
          </p>
          <div className="h-4 rounded-full bg-charcoal overflow-hidden border border-leather">
            <div
              className="h-full rounded-full bg-gold transition-all duration-500"
              style={{ width: `${advancementPct}%` }}
            />
          </div>
          <p className="text-sm text-gold/90 mt-2">
            ${revenue.toLocaleString()} / ${threshold.toLocaleString()} ({advancementPct}%)
          </p>
          {tierProgression?.eligible_for_promotion && (
            <p className="text-sm text-gold font-medium mt-2">{tierProgression.message}</p>
          )}
        </section>
      )}

      {partner.tier === 'Certified Broker' && (
        <section className="rounded-xl border border-gold/40 bg-gold-muted p-4">
          <p className="text-gold font-medium">Certified Broker · 5% base commission on collected revenue</p>
        </section>
      )}

      <section>
        <h2 className="text-lg font-medium text-gold mb-4">Partner activity</h2>
        <PartnerActivityForm partnerId={partner.id} partnerTier={partner.tier} commissionRate={partner.commission_rate} onSuccess={refresh} />
      </section>

      <section>
        <h2 className="text-lg font-medium text-gold mb-4">Production reports</h2>
        <div className="rounded-lg overflow-hidden border border-leather bg-leather/30">
          {reports.length === 0 ? (
            <div className="px-4 py-8 text-center text-station-muted text-sm">
              No reports yet. Submit activity above.
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-leather text-gold/90 text-sm">
                <tr>
                  <th className="px-4 py-3 font-medium">Account</th>
                  <th className="px-4 py-3 font-medium">Cases</th>
                  <th className="px-4 py-3 font-medium">Collected revenue</th>
                  <th className="px-4 py-3 font-medium">New account</th>
                  <th className="px-4 py-3 font-medium">Reported</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-leather/50">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-leather/20">
                    <td className="px-4 py-3 font-medium text-white">{r.account_name}</td>
                    <td className="px-4 py-3 text-station-muted">{r.case_volume}</td>
                    <td className="px-4 py-3 text-station-muted">
                      ${r.collected_revenue.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-station-muted">
                      {r.is_new_account ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-3 text-station-muted text-sm">
                      {new Date(r.reported_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
